<div [formGroup]="form">

    <!-- Identity: server-assigned on save, so read-only and shown as a heading -->
    <p class="fsr-identity">
        <ng-container *ngIf="reportSerialNo || jobId; else identityPending">
            <strong>Report Serial No. {{ reportSerialNo || '-' }}</strong>
            <span class="sep">&middot;</span>
            <strong>Job ID {{ jobId || '-' }}</strong>
        </ng-container>
        <ng-template #identityPending>
            <span class="text-muted">Report Serial No. and Job ID are assigned on save.</span>
        </ng-template>
    </p>

    <div class="row">
        <!-- ============================ LEFT ============================ -->
        <div class="col-lg-6">

            <div class="mb-3 form-styles">
                <label>Job Visit Type <span class="text-danger">*</span></label>
                <ng-select formControlName="job_visit_type" placeholder="Select Job"
                    [items]="jobVisitTypes" bindLabel="name" bindValue="id">
                </ng-select>
                <div *ngIf="invalid('job_visit_type')" class="text-danger small">
                    <span *ngIf="form.get('job_visit_type')?.errors?.['required']">Job Visit Type is required</span>
                </div>
            </div>

            <div class="mb-3 form-styles">
                <label>Location (Plant Name) <span class="text-danger">*</span></label>
                <ng-select formControlName="plant" placeholder="Select Location" [items]="plantList"
                    bindLabel="stationName" bindValue="id" [readonly]="plantLocked"
                    (change)="plantChange.emit($event)">
                </ng-select>

                <!-- City and region come with the plant, same as the ticket form -->
                <div class="d-flex flex-wrap gap-2 mt-2" *ngIf="form.get('plant')?.value">
                    <span class="badge badge-custom shadow">
                        Region: {{ form.get('region')?.value || '-' }}
                    </span>
                    <span class="badge badge-custom shadow">
                        City: {{ form.get('city')?.value || '-' }}
                    </span>
                </div>

                <small class="text-muted d-block" *ngIf="plantLocked">Inherited from the ticket.</small>
                <div *ngIf="invalid('plant')" class="text-danger small">
                    <span *ngIf="form.get('plant')?.errors?.['required']">Location is required</span>
                </div>
            </div>

            <div class="mb-3 form-styles">
                <label>Customer Name
                    <span class="text-danger" *ngIf="customerFieldState === 'multiple'">*</span>
                </label>

                <!-- One customer on the plant: nothing to choose -->
                <input *ngIf="customerFieldState === 'single'" type="text" class="form-control"
                    [value]="selectedCustomerName" readonly>

                <!-- Several: the user picks who gets the completion email -->
                <ng-select *ngIf="customerFieldState === 'multiple'" formControlName="customer"
                    placeholder="Select Customer" [items]="customerList" bindLabel="full_name"
                    bindValue="id">
                </ng-select>

                <input *ngIf="customerFieldState === 'none'" type="text" class="form-control"
                    value="No customer linked to this plant" readonly>
                <small class="text-muted" *ngIf="customerFieldState === 'none'">
                    The completion email can only be sent when the plant has a customer.
                </small>
            </div>

            <div class="mb-3 form-styles">
                <label>Complaint Type</label>
                <ng-select formControlName="complaint_type" placeholder="Select Type"
                    [items]="complaintTypes" bindLabel="name" bindValue="id">
                </ng-select>
            </div>

            <!-- An installation can mix panel types, so this is a repeatable
                 (No. of Panels x Panel Capacity) row rather than one pair. -->
            <div formArrayName="panel_groups">
                <div class="row align-items-start" *ngFor="let group of panelGroups.controls; let i = index"
                    [formGroupName]="i">
                    <div class="col-md-5 mb-3">
                        <label *ngIf="i === 0">Panel Capacity <span class="text-danger">*</span></label>
                        <input type="number" step="0.001" min="0.001" class="form-control"
                            formControlName="panel_capacity" placeholder="Add Capacity">
                        <div *ngIf="panelGroupInvalid(i, 'panel_capacity')" class="text-danger small">
                            <span *ngIf="group.get('panel_capacity')?.errors?.['required']">Panel Capacity is
                                required</span>
                            <span *ngIf="group.get('panel_capacity')?.errors?.['min']">Must be greater than 0</span>
                        </div>
                    </div>
                    <div class="col-md-5 mb-3">
                        <label *ngIf="i === 0">No. of Panels <span class="text-danger">*</span></label>
                        <input type="number" step="1" min="1" class="form-control"
                            formControlName="number_of_panels" placeholder="Add Panels">
                        <div *ngIf="panelGroupInvalid(i, 'number_of_panels')" class="text-danger small">
                            <span *ngIf="group.get('number_of_panels')?.errors?.['required']">No. of Panels is
                                required</span>
                            <span *ngIf="group.get('number_of_panels')?.errors?.['min']">Must be at least 1</span>
                        </div>
                    </div>
                    <div class="col-md-2 mb-3">
                        <!-- Invisible label keeps the buttons level with the inputs on the first row. -->
                        <label *ngIf="i === 0" class="d-block invisible" aria-hidden="true">&nbsp;</label>
                        <!-- Remove comes first so it stays in the same spot on every row. -->
                        <div class="panel-group-actions">
                            <button type="button" class="btn btn-outline-danger panel-icon-btn" title="Remove this row"
                                *ngIf="panelGroups.length > 1" (click)="removePanelGroup.emit(i)">
                                <i class="fa fa-times"></i>
                            </button>
                            <button type="button" class="btn btn-outline-dark panel-icon-btn"
                                title="Add another panel group" *ngIf="i === panelGroups.length - 1"
                                (click)="addPanelGroup.emit()">
                                <i class="fa fa-plus"></i>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div class="mb-3">
                <label>System Capacity DC</label>
                <input type="text" class="form-control" formControlName="system_capacity_dc"
                    placeholder="Calculated" readonly>
                <small class="text-muted">Total Panel Capacity = &Sigma; (No. of Panels &times; Panel
                    Capacity)</small>
            </div>

            <h6 class="fsr-section-title">Staff Information</h6>

            <div class="row">
                <div class="col-md-6 mb-3 form-styles">
                    <label>ZC Staff Name</label>
                    <ng-select formControlName="assigned_to" placeholder="Name" [items]="staffList"
                        bindLabel="full_name" bindValue="id">
                    </ng-select>
                </div>
                <!-- bsDatepicker works in Date objects; the control keeps its
                     'YYYY-MM-DD' string, bridged in the component. -->
                <div class="col-md-6 mb-3 form-styles">
                    <label>Visit Date</label>
                    <div class="input-group daterange-wrapper">
                        <input type="text" class="form-control fsr-date-input" bsDatepicker #visitDp="bsDatepicker"
                            [bsConfig]="visitDateConfig" [bsValue]="visitDate"
                            (bsValueChange)="onVisitDateChange($event)" placeholder="Select visit date" readonly>
                        <div class="input-group-append" (click)="visitDp.toggle()">
                            <span><svg xmlns="http://www.w3.org/2000/svg" width="15" height="16" viewBox="0 0 15 16"
                                    fill="none">
                                    <path
                                        d="M12.8046 1.60066H12.0042V0H10.4036V1.60066H4.00087V0H2.40021V1.60066H1.59987C1.39011 1.60066 1.18241 1.64213 0.988734 1.72268C0.795054 1.80324 0.619218 1.9213 0.471327 2.07006C0.323435 2.21882 0.206406 2.39535 0.126986 2.5895C0.0475649 2.78365 0.00731745 2.99157 0.00854826 3.20133L0.000549316 14.3993C0.000549316 14.8239 0.169189 15.231 0.469373 15.5312C0.769556 15.8314 1.17669 16 1.60121 16H12.7992C13.2237 16 13.6309 15.8314 13.9311 15.5312C14.2312 15.231 14.3999 14.8239 14.3999 14.3993V3.20133C14.3999 2.77773 14.232 2.37141 13.933 2.07138C13.6339 1.77135 13.2282 1.60208 12.8046 1.60066ZM12.8046 14.398H1.60655V5.59433H12.8046V14.398Z"
                                        fill="currentColor" />
                                </svg></span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- app-time-picker is a ControlValueAccessor over ngb-timepicker,
                 so these controls still hold plain 'HH:MM:SS' strings. -->
            <div class="row">
                <div class="col-md-6 mb-3">
                    <label>Time In</label>
                    <app-time-picker formControlName="time_in" placeholder="Select time in">
                    </app-time-picker>
                </div>
                <div class="col-md-6 mb-3">
                    <label>Time Out</label>
                    <app-time-picker formControlName="time_out" placeholder="Select time out">
                    </app-time-picker>
                </div>
            </div>
            <div *ngIf="form.errors?.['timePair']" class="text-danger small mb-3">
                Time In and Time Out must both be provided
            </div>

            <div class="mb-3">
                <label>Total Worked Hours</label>
                <input type="text" class="form-control" formControlName="total_worked_hours"
                    placeholder="HH:MM:SS" readonly>
                <small class="text-muted">A Time Out before Time In counts as the next day.</small>
            </div>

            <div class="mb-3">
                <label>Description of Job</label>
                <textarea formControlName="description_of_job" rows="4" maxlength="2000"
                    class="form-control no-resize" placeholder="Add Description" style="height: 110px;"></textarea>
                <div *ngIf="invalid('description_of_job')" class="text-danger small">
                    <span *ngIf="form.get('description_of_job')?.errors?.['maxlength']">Maximum 2000 characters
                        allowed</span>
                </div>
            </div>
        </div>

        <!-- ============================ RIGHT =========================== -->
        <div class="col-lg-6">

            <h6 class="fsr-section-title">Checks</h6>
            <div class="fsr-check-table">
                <table class="table-data text-center">
                    <thead>
                        <tr>
                            <th scope="col">Module Cleaning</th>
                            <th scope="col">Loose Connections</th>
                            <th scope="col">Inverter Cleaning</th>
                            <th scope="col">Module Damage</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td><input class="form-check-input static med" type="checkbox"
                                    formControlName="check_module_cleaning"></td>
                            <td><input class="form-check-input static med" type="checkbox"
                                    formControlName="check_loose_connections"></td>
                            <td><input class="form-check-input static med" type="checkbox"
                                    formControlName="check_inverter_cleaning"></td>
                            <td><input class="form-check-input static med" type="checkbox"
                                    formControlName="check_module_damage"></td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <h6 class="fsr-section-title">Replacement</h6>
            <div class="fsr-check-table">
                <table class="table-data text-center">
                    <thead>
                        <tr>
                            <th scope="col">Broken Panel</th>
                            <th scope="col">MC4</th>
                            <th scope="col">DC Cable</th>
                            <th scope="col">AC Cable</th>
                            <th scope="col">Others</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td><input class="form-check-input static med" type="checkbox"
                                    formControlName="repl_broken_panel"></td>
                            <td><input class="form-check-input static med" type="checkbox"
                                    formControlName="repl_mc4"></td>
                            <td><input class="form-check-input static med" type="checkbox"
                                    formControlName="repl_dc_cable"></td>
                            <td><input class="form-check-input static med" type="checkbox"
                                    formControlName="repl_ac_cable"></td>
                            <td><input class="form-check-input static med" type="checkbox"
                                    formControlName="repl_others"></td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Always visible, but only required once "Others" is ticked -->
            <div class="mb-3">
                <label>Other Description
                    <span class="text-danger" *ngIf="form.get('repl_others')?.value">*</span>
                </label>
                <textarea formControlName="repl_others_detail" rows="3" maxlength="500"
                    class="form-control no-resize" placeholder="Add Description" style="height: 90px;"></textarea>
                <div *ngIf="invalid('repl_others_detail')" class="text-danger small">
                    <span *ngIf="form.get('repl_others_detail')?.errors?.['required']">Please describe the other
                        replacement</span>
                    <span *ngIf="form.get('repl_others_detail')?.errors?.['maxlength']">Maximum 500 characters
                        allowed</span>
                </div>
            </div>

            <h6 class="fsr-section-title">General Remarks</h6>
            <div class="mb-3">
                <label>Remarks</label>
                <textarea formControlName="remarks" rows="4" maxlength="2000" class="form-control no-resize"
                    placeholder="Add Description" style="height: 110px;"></textarea>
            </div>

            <h6 class="fsr-section-title">
                Attachments
                <small class="text-muted fw-normal">(images, videos and documents; 100MB total)</small>
            </h6>

            <!-- Already uploaded -->
            <div class="file-pills-container mb-2" *ngIf="existingAttachments?.length">
                <a *ngFor="let attachment of existingAttachments" class="file-pill" target="_blank"
                    rel="noopener" [href]="attachment.file_url">
                    <span class="file-name">{{ attachment.file_name }}</span>
                </a>
            </div>

            <label class="fsr-dropzone" [class.is-dragging]="isDragging" for="fsrFileInput"
                (dragover)="onDragOver($event)" (dragleave)="onDragLeave($event)" (drop)="onDrop($event)">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M21 15V19C21 20.1 20.1 21 19 21H5C3.9 21 3 20.1 3 19V15" stroke="currentColor"
                        stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M7 9L12 4L17 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"
                        stroke-linejoin="round" />
                    <path d="M12 4V16" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"
                        stroke-linejoin="round" />
                </svg>
                <span>{{ isDragging ? 'Drop to attach' : 'Click to upload file' }}</span>
                <input type="file" id="fsrFileInput" class="file-input-hidden" multiple
                    (change)="onFileInputChange($event)">
            </label>

            <!-- Queued for this save -->
            <div class="file-pills-container" *ngIf="files?.length">
                <div *ngFor="let file of files; let i = index" class="file-pill">
                    <span class="file-name">{{ file.name }} ({{ formatFileSize(file.size) }})</span>
                    <button type="button" class="pill-close-btn" (click)="fileRemove.emit(i)">
                        <i class="fa fa-times"></i>
                    </button>
                </div>
            </div>

            <small *ngIf="files?.length" [class.text-danger]="pendingUploadExceedsLimit"
                [class.text-muted]="!pendingUploadExceedsLimit">
                {{ files.length }} file(s), {{ formatFileSize(pendingUploadSize) }} total.
                <span *ngIf="pendingUploadExceedsLimit">
                    Over the 100MB limit — you will be asked whether to compress on save.
                </span>
            </small>
        </div>
    </div>
</div>
